import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  Container,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Login as LoginIcon,
  School as SchoolIcon,
  Leaderboard as LeaderboardIcon,
  Dashboard as DashboardIcon,
  EmojiEvents as ContestIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { COUNTRIES } from '@utils/countries';
import { getStatesForCountry } from '@utils/states';
import { CORE_SPORTS, isShootingSport, toSportLabel, toSportSlug } from '@utils/sports';
import { useAuthStore } from '@store/authStore';
import VideoPlayer from '@components/VideoPlayer';
import { StrokeVideoSectionsDisplay } from '@components/StrokeVideoSectionsDisplay';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import { BadmintonEnglandPhysicalBenchmarkTable } from '@components/sport/BadmintonEnglandPhysicalBenchmarkTable';
import { BadmintonMentalHealthSection } from '@components/sport/BadmintonMentalHealthSection';
import { BadmintonPlayerMisconductTable } from '@components/sport/BadmintonPlayerMisconductTable';
import { BadmintonStrengthConditioningTable } from '@components/sport/BadmintonStrengthConditioningTable';
import { CoachingPedagogyTable } from '@components/sport/CoachingPedagogyTable';
import { EnjoymentEntertainmentTable } from '@components/sport/EnjoymentEntertainmentTable';
import { IceHockeyPhysicalBenchmarkTable } from '@components/sport/IceHockeyPhysicalBenchmarkTable';
import { TalentScoreCardTable } from '@components/sport/TalentScoreCardTable';
import { TrainingMethodsTable } from '@components/sport/TrainingMethodsTable';
import { generateManualAssessmentForm } from '@utils/generateManualAssessmentForm';
import {
  ContestHubHeader,
  ContestSponsorBanner,
  ContestTaskCard,
  FeaturedSmashContest,
} from '@components/contest/ContestHubUI';
import { CONTEST_REGISTRATION_TEXT } from '@utils/contest';
import { useSportModules } from '@hooks/useSportModules';
import SportModuleComingSoon from '@components/sport/SportModuleComingSoon';
import {
  getModuleState,
  getVisiblePublicTabs,
  isContentEnabled,
  type SportPublicTabId,
} from '@utils/sportModules';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type FrameworkType = 'regular' | 'para';

interface ContestVideoItem {
  title?: string;
  url?: string;
  name?: string;
  notes?: string;
  sponsorName?: string;
  sponsorLogoUrl?: string;
}

interface StrokeSponsor {
  sponsorName: string;
  sponsorLogoUrl: string;
}

interface ContestTargetItem {
  type?: string;
  code?: string;
  name?: string;
  sequence?: number;
}

interface PublicContestTask {
  id: string;
  title: string;
  description?: string;
  instructions?: string;
  targetItems?: ContestTargetItem[];
  participationFormat?: string;
  regularTrainingVideos?: ContestVideoItem[];
  creatorReferenceVideos?: ContestVideoItem[];
  amateurSampleVideos?: ContestVideoItem[];
  performanceSubmissionEnabled?: boolean;
  scoringNotes?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sport-tabpanel-${index}`}
      aria-labelledby={`sport-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SportPage: React.FC = () => {
  const { sportName } = useParams<{ sportName: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [rubricTabValue, setRubricTabValue] = useState(0);
  const [learningTabValue, setLearningTabValue] = useState(0); // 0: Strokes, 1: Drills, 2: Fitness
  const [rankingTypeTab, setRankingTypeTab] = useState(0); // 0: Coach/Academy, 1: Board Exam (Self Evaluation and Tournament hidden for public)
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [rankingLevel, setRankingLevel] = useState<'national' | 'state'>('national');
  const [selectedAgeCategory, setSelectedAgeCategory] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  // Public leaderboards are YEARLY only (periodKey = Y-YYYY).
  const [rankingPeriodKey, setRankingPeriodKey] = useState<string>(''); // '' = latest submitted yearly
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [playerNameSearch, setPlayerNameSearch] = useState<string>('');
  const sportDisplayName = sportName ? toSportLabel(sportName) : 'Sport';
  const { isAuthenticated, user } = useAuthStore();
  const { data: sportModuleMatrix } = useSportModules();
  const moduleCtx = React.useMemo(
    () => ({
      isAuthenticated,
      isAdmin: ['admin', 'super_admin', 'evaluation_admin', 'contest_admin'].includes(
        String(user?.role || '')
      ),
    }),
    [isAuthenticated, user?.role]
  );
  const visibleSportTabs = React.useMemo(
    () => getVisiblePublicTabs(sportModuleMatrix, sportDisplayName, moduleCtx),
    [sportModuleMatrix, sportDisplayName, moduleCtx]
  );
  const activeModuleId: SportPublicTabId =
    visibleSportTabs[tabValue]?.id ?? visibleSportTabs[0]?.id ?? 'rubric';
  const sportModuleState = (moduleId: string) =>
    getModuleState(sportModuleMatrix, sportDisplayName, moduleId);
  const sportModuleEnabled = (moduleId: string) =>
    isContentEnabled(sportModuleState(moduleId), moduleCtx);
  const [strokeTypes, setStrokeTypes] = useState<any[]>([]);
  const [allStrokeTypes, setAllStrokeTypes] = useState<any[]>([]); // Store all strokes for filtering
  const [loadingStrokes, setLoadingStrokes] = useState(false);
  
  // Filter states for strokes
  const [filterCourtPosition, setFilterCourtPosition] = useState<string>('');
  const [filterHalfSide, setFilterHalfSide] = useState<string>('');
  const [filterStrokeType, setFilterStrokeType] = useState<string>('');
  const [filterPlayType, setFilterPlayType] = useState<string>('');
  const [filterShotType, setFilterShotType] = useState<string>('');
  const [filterDifficulty, setFilterDifficulty] = useState<string>('');
  const [badmintonFramework, setBadmintonFramework] = useState<FrameworkType>('regular');
  const [badmintonParaClass, setBadmintonParaClass] = useState<string>('WH1');
  const [journeyTab, setJourneyTab] = useState<'strokes' | 'drills' | 'fitness'>('strokes');
  const [footballRoleTab, setFootballRoleTab] = useState<'skills' | 'single-drills' | 'mixed-drills' | 'fitness'>('skills');
  const [homePreview, setHomePreview] = useState<{ skills: any[]; strokes: any[]; drills: any[] }>({
    skills: [],
    strokes: [],
    drills: [],
  });
  const [selfPracticePreview, setSelfPracticePreview] = useState<any[]>([]);
  const [coachingPreview, setCoachingPreview] = useState<any[]>([]);
  const [strengthConditioningPreview, setStrengthConditioningPreview] = useState<any[]>([]);
  const [mentalHealthPreview, setMentalHealthPreview] = useState<any[]>([]);
  const [selfResponsibilityPreview, setSelfResponsibilityPreview] = useState<any[]>([]);
  const [rulesFitnessPreview, setRulesFitnessPreview] = useState<any[]>([]);
  const [stemLearningPreview, setStemLearningPreview] = useState<any[]>([]);
  const [dataAnalyticsAiPreview, setDataAnalyticsAiPreview] = useState<any[]>([]);
  const [milestoneLeaderboardPreview, setMilestoneLeaderboardPreview] = useState<any[]>([]);
  const [teamLeaguePreview, setTeamLeaguePreview] = useState<any[]>([]);
  const [tournamentPreview, setTournamentPreview] = useState<any[]>([]);
  const [contestTasks, setContestTasks] = useState<PublicContestTask[]>([]);
  const [loadingContestTasks, setLoadingContestTasks] = useState(false);
  
  const isBadmintonSport = sportDisplayName.toLowerCase() === 'badminton';
  const isFootballSport = sportDisplayName.toLowerCase() === 'football';
  const isIceHockeySport = sportDisplayName.toLowerCase() === 'ice hockey';
  const isShootingSportView = isShootingSport(sportDisplayName);
  const coreSkillLabel = isIceHockeySport ? 'Skills' : isShootingSportView ? 'Shooting' : 'Strokes & Shots';
  const strokeTableLabels = isShootingSportView
    ? {
        sectionTitle: `${sportDisplayName} Shooting`,
        courtPosition: 'Goal Target Zone',
        halfSide: 'Shooting Position',
        strokeType: 'Player Position',
        strokeAction: 'Distance (yards)',
        playType: 'Shooting Method',
        shotType: 'Area of Improvement',
        placement: 'Possible Pitfalls',
        difficulty: 'Shooting Rating',
      }
    : {
        sectionTitle: `${sportDisplayName} Strokes`,
        courtPosition: 'Court Position',
        halfSide: 'Half Court',
        strokeType: 'Stroke Type',
        strokeAction: 'Stroke Action',
        playType: 'Play Type',
        shotType: 'Shot Type',
        placement: 'Placement',
        difficulty: 'Difficulty',
      };
  const cardHomePreview = homePreview || { skills: [], strokes: [], drills: [] };
  const cardFitnessPreviewItems = (cardHomePreview.skills || []).filter((item: any) =>
    String(item.category || '').toLowerCase().includes('fitness')
  );
  const strokesLabel = sportDisplayName.toLowerCase() === 'badminton'
    ? '200+'
    : `${Math.max(0, (cardHomePreview.strokes || []).length)}+`;
  const drillsLabel = sportDisplayName.toLowerCase() === 'badminton'
    ? '60+'
    : `${Math.max(0, (cardHomePreview.drills || []).length)}+`;
  const fitnessLabel = sportDisplayName.toLowerCase() === 'badminton'
    ? '40+'
    : `${Math.max(0, cardFitnessPreviewItems.length)}+`;
  const footballCoreSkillCounts = {
    shooting: '32+',
    passingReceiving: '41+',
    dribbling: '38+',
    heading: '24+',
    turning: '27+',
    goalkeeper: '22+',
  };
  const footballRoleLabels = [
    'Goalkeeper (1)',
    'Left-back (2)',
    'Right-back (3)',
    'Centre-Back (4)',
    'Centre-Back (5)',
    'Defensive Midfielder (6)',
    'Right Winger (7)',
    'Central Midfielder (8)',
    'Striker (9)',
    'Attacking Midfielder (10)',
    'Left Winger (11)',
  ];
  const footballSkillColumns = ['Shooting', 'Dribbling', 'Passing & Receiving', 'Heading', 'Turning'];
  const footballRoleSkillRows = [
    { role: 'Goalkeeper (1)', shooting: '6', dribbling: '7', passingReceiving: '9', heading: '5', turning: '8' },
    { role: 'Left-back (2)', shooting: '9', dribbling: '12', passingReceiving: '15', heading: '8', turning: '11' },
    { role: 'Right-back (3)', shooting: '10', dribbling: '13', passingReceiving: '14', heading: '8', turning: '12' },
    { role: 'Centre-Back (4)', shooting: '7', dribbling: '8', passingReceiving: '11', heading: '16', turning: '9' },
    { role: 'Centre-Back (5)', shooting: '7', dribbling: '8', passingReceiving: '12', heading: '17', turning: '9' },
    { role: 'Defensive Midfielder (6)', shooting: '10', dribbling: '12', passingReceiving: '18', heading: '10', turning: '13' },
    { role: 'Right Winger (7)', shooting: '14', dribbling: '18', passingReceiving: '13', heading: '7', turning: '15' },
    { role: 'Central Midfielder (8)', shooting: '12', dribbling: '14', passingReceiving: '19', heading: '9', turning: '14' },
    { role: 'Striker (9)', shooting: '20', dribbling: '16', passingReceiving: '11', heading: '13', turning: '15' },
    { role: 'Attacking Midfielder (10)', shooting: '17', dribbling: '17', passingReceiving: '16', heading: '8', turning: '18' },
    { role: 'Left Winger (11)', shooting: '15', dribbling: '19', passingReceiving: '14', heading: '7', turning: '16' },
  ];
  const footballRoleSingleSkillDrillRows = [
    { role: 'Goalkeeper (1)', shooting: '5', dribbling: '6', passingReceiving: '7', heading: '5', turning: '6' },
    { role: 'Left-back (2)', shooting: '8', dribbling: '11', passingReceiving: '12', heading: '7', turning: '10' },
    { role: 'Right-back (3)', shooting: '8', dribbling: '10', passingReceiving: '12', heading: '7', turning: '10' },
    { role: 'Centre-Back (4)', shooting: '6', dribbling: '7', passingReceiving: '9', heading: '14', turning: '8' },
    { role: 'Centre-Back (5)', shooting: '6', dribbling: '7', passingReceiving: '9', heading: '15', turning: '8' },
    { role: 'Defensive Midfielder (6)', shooting: '9', dribbling: '10', passingReceiving: '16', heading: '9', turning: '11' },
    { role: 'Right Winger (7)', shooting: '13', dribbling: '17', passingReceiving: '11', heading: '6', turning: '14' },
    { role: 'Central Midfielder (8)', shooting: '11', dribbling: '12', passingReceiving: '17', heading: '8', turning: '12' },
    { role: 'Striker (9)', shooting: '18', dribbling: '14', passingReceiving: '9', heading: '12', turning: '13' },
    { role: 'Attacking Midfielder (10)', shooting: '15', dribbling: '15', passingReceiving: '14', heading: '7', turning: '16' },
    { role: 'Left Winger (11)', shooting: '14', dribbling: '18', passingReceiving: '12', heading: '6', turning: '15' },
  ];
  const footballRoleMixedSkillDrillRows = [
    { role: 'Goalkeeper (1)', count: '6' },
    { role: 'Left-back (2)', count: '11' },
    { role: 'Right-back (3)', count: '10' },
    { role: 'Centre-Back (4)', count: '9' },
    { role: 'Centre-Back (5)', count: '9' },
    { role: 'Defensive Midfielder (6)', count: '13' },
    { role: 'Right Winger (7)', count: '16' },
    { role: 'Central Midfielder (8)', count: '15' },
    { role: 'Striker (9)', count: '14' },
    { role: 'Attacking Midfielder (10)', count: '17' },
    { role: 'Left Winger (11)', count: '16' },
  ];
  const footballRoleFitnessRows = [
    { role: 'Goalkeeper (1)', count: '8' },
    { role: 'Left-back (2)', count: '13' },
    { role: 'Right-back (3)', count: '13' },
    { role: 'Centre-Back (4)', count: '12' },
    { role: 'Centre-Back (5)', count: '12' },
    { role: 'Defensive Midfielder (6)', count: '15' },
    { role: 'Right Winger (7)', count: '18' },
    { role: 'Central Midfielder (8)', count: '17' },
    { role: 'Striker (9)', count: '16' },
    { role: 'Attacking Midfielder (10)', count: '19' },
    { role: 'Left Winger (11)', count: '18' },
  ];

  const handleDownloadSportAssessmentForm = async (
    trainingLevel: 'beginner' | 'advanced',
    framework: FrameworkType = 'regular',
    paraClass = 'WH1'
  ) => {
    await generateManualAssessmentForm(
      { id: 'sport-page-preview-player', firstName: '', lastName: '', academyName: 'N/A' },
      sportDisplayName,
      'master-training',
      framework === 'para' ? paraClass : 'N/A',
      trainingLevel
    );
  };

  const renderTrainingPreviewSection = (
    title: string,
    emptyLabel: string,
    items: any[],
    headerBg: string,
    headerBorder: string
  ) => {
    const sectionContent = (
      <>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {items.slice(0, 6).map((item: any) => (
            <Box key={item.id} sx={{ border: '1px solid #dbe8f6', borderRadius: 1, px: 1, py: 0.7, bgcolor: '#fff' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.title}</Typography>
              {item.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.description}
                </Typography>
              )}
              {item.tips && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {item.tips}
                </Typography>
              )}
              {Array.isArray(item.referenceVideos) && item.referenceVideos.length > 0 && (
                <Box sx={{ mt: 0.25 }}>
                  <VideoPlayer url={item.referenceVideos[0]} />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      </>
    );

    return renderRubricAccordion(title, `${items.length}`, sectionContent, headerBg, headerBorder);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (tabValue >= visibleSportTabs.length) {
      setTabValue(0);
    }
  }, [sportDisplayName, visibleSportTabs.length, tabValue]);

  const sportTabIconByModule: Record<SportPublicTabId, React.ReactElement> = {
    rubric: <DashboardIcon />,
    learning: <SchoolIcon />,
    ranking: <LeaderboardIcon />,
    contests: <ContestIcon />,
  };

  const handleLearningTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setLearningTabValue(newValue);
  };

  const handleRankingTypeTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setRankingTypeTab(newValue);
  };

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCountry(event.target.value);
    setSelectedState(''); // Reset state when country changes
  };

  const handleStateChange = (event: SelectChangeEvent<string>) => {
    setSelectedState(event.target.value);
  };

  const handleRankingLevelChange = (event: SelectChangeEvent<string>) => {
    const level = event.target.value as 'national' | 'state';
    setRankingLevel(level);
    if (level === 'national') {
      setSelectedState(''); // Clear state filter for national rankings
    }
  };

  const handleAgeCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedAgeCategory(event.target.value);
  };

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    const y = event.target.value;
    setSelectedYear(y);
    if (y) {
      setRankingPeriodKey(`Y-${y}`);
    } else {
      // All years -> let backend choose latest YEARLY period
      setRankingPeriodKey('');
    }
  };

  const handleGenderChange = (event: SelectChangeEvent<string>) => {
    setSelectedGender(event.target.value);
  };

  const handlePlayerNameSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerNameSearch(event.target.value);
  };

  const availableStates = getStatesForCountry(selectedCountry);
  
  // Generate years list (current year and past 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i).map(String);
  
  // Age categories
  const ageCategories = ['U8', 'U9', 'U11', 'U13', 'U15', 'U17', 'U19', 'Senior', 'Master', 'Veteran'];
  
  // Gender options
  const genderOptions = ['All', 'Boys', 'Girls'];

  // Helper function to convert difficulty to number (1-5) for star rating
  const normalizeDifficultyValue = (value: string | number | undefined | null): number => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string' && value.trim() === '') return 0;
    
    // If already a number, ensure it's 0-5
    if (typeof value === 'number') {
      return Math.max(0, Math.min(5, Math.round(value)));
    }
    
    // If string, try to parse as number first
    const str = String(value).trim();
    const num = parseInt(str, 10);
    if (!isNaN(num) && num >= 0 && num <= 5) {
      return num;
    }
    
    // Legacy string values - convert to numbers
    const lowerStr = str.toLowerCase();
    if (lowerStr === 'low' || lowerStr === '1') return 1;
    if (lowerStr === 'medium' || lowerStr === '2' || lowerStr === '3') return 3;
    if (lowerStr === 'high' || lowerStr === '4' || lowerStr === '5') return 5;
    
    // Default to 0 if can't parse
    return 0;
  };

  // Load stroke types for Learning tab.
  useEffect(() => {
    if (sportDisplayName.toLowerCase() === 'badminton' && activeModuleId === 'learning' && learningTabValue === 0) {
      loadStrokeTypes();
    }
  }, [sportDisplayName, activeModuleId, learningTabValue, isAuthenticated]);

  const loadStrokeTypes = async () => {
    setLoadingStrokes(true);
    try {
      const response = await fetch('/api/admin/ranking-stroke-types');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter only active strokes
          const activeStrokes = data.data.filter((st: any) => st.isActive !== false);
          // Sort by display order
          activeStrokes.sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
          
          // Store all strokes
          setAllStrokeTypes(activeStrokes);
          
          // Show all rows - no pagination
          setStrokeTypes(activeStrokes);
        }
      }
    } catch (error) {
      console.error('Error loading stroke types:', error);
    } finally {
      setLoadingStrokes(false);
    }
  };

  const loadHomepagePreview = async (sport: string, framework: FrameworkType = 'regular', paraClass = 'WH1') => {
    try {
      const response = await fetch(
        `/api/landing/homepage-preview?sport=${encodeURIComponent(sport)}&framework=${encodeURIComponent(framework)}&paraClass=${encodeURIComponent(paraClass)}&limit=10`
      );
      const data = await response.json();
      if (data?.success) {
        setHomePreview({
          skills: Array.isArray(data?.data?.skills) ? data.data.skills : [],
          strokes: Array.isArray(data?.data?.strokes) ? data.data.strokes : [],
          drills: Array.isArray(data?.data?.drills) ? data.data.drills : [],
        });
      } else {
        setHomePreview({ skills: [], strokes: [], drills: [] });
      }
    } catch {
      setHomePreview({ skills: [], strokes: [], drills: [] });
    }
  };

  const loadTrainingContentPreview = async (sport: string) => {
    try {
      const [
        selfPracticeRes,
        coachingRes,
        strengthConditioningRes,
        mentalHealthRes,
        selfResponsibilityRes,
        rulesFitnessRes,
        stemLearningRes,
        dataAnalyticsAiRes,
        milestoneLeaderboardRes,
        teamLeagueRes,
        tournamentRes,
      ] = await Promise.all([
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=self-practice`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=coaching`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=strength-conditioning`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=mental-health`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=self-responsibility`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=rules-fitness`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=stem-learning`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=data-analytics-ai`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=milestone-leaderboard`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=team-league`),
        fetch(`/api/training-content-items?sport=${encodeURIComponent(sport)}&contentType=tournament`),
      ]);

      const [
        selfPracticeData,
        coachingData,
        strengthConditioningData,
        mentalHealthData,
        selfResponsibilityData,
        rulesFitnessData,
        stemLearningData,
        dataAnalyticsAiData,
        milestoneLeaderboardData,
        teamLeagueData,
        tournamentData,
      ] = await Promise.all([
        selfPracticeRes.json(),
        coachingRes.json(),
        strengthConditioningRes.json(),
        mentalHealthRes.json(),
        selfResponsibilityRes.json(),
        rulesFitnessRes.json(),
        stemLearningRes.json(),
        dataAnalyticsAiRes.json(),
        milestoneLeaderboardRes.json(),
        teamLeagueRes.json(),
        tournamentRes.json(),
      ]);

      setSelfPracticePreview(selfPracticeData?.success && Array.isArray(selfPracticeData?.data) ? selfPracticeData.data : []);
      setCoachingPreview(coachingData?.success && Array.isArray(coachingData?.data) ? coachingData.data : []);
      setStrengthConditioningPreview(strengthConditioningData?.success && Array.isArray(strengthConditioningData?.data) ? strengthConditioningData.data : []);
      setMentalHealthPreview(mentalHealthData?.success && Array.isArray(mentalHealthData?.data) ? mentalHealthData.data : []);
      setSelfResponsibilityPreview(selfResponsibilityData?.success && Array.isArray(selfResponsibilityData?.data) ? selfResponsibilityData.data : []);
      setRulesFitnessPreview(rulesFitnessData?.success && Array.isArray(rulesFitnessData?.data) ? rulesFitnessData.data : []);
      setStemLearningPreview(stemLearningData?.success && Array.isArray(stemLearningData?.data) ? stemLearningData.data : []);
      setDataAnalyticsAiPreview(dataAnalyticsAiData?.success && Array.isArray(dataAnalyticsAiData?.data) ? dataAnalyticsAiData.data : []);
      setMilestoneLeaderboardPreview(milestoneLeaderboardData?.success && Array.isArray(milestoneLeaderboardData?.data) ? milestoneLeaderboardData.data : []);
      setTeamLeaguePreview(teamLeagueData?.success && Array.isArray(teamLeagueData?.data) ? teamLeagueData.data : []);
      setTournamentPreview(tournamentData?.success && Array.isArray(tournamentData?.data) ? tournamentData.data : []);
    } catch {
      setSelfPracticePreview([]);
      setCoachingPreview([]);
      setStrengthConditioningPreview([]);
      setMentalHealthPreview([]);
      setSelfResponsibilityPreview([]);
      setRulesFitnessPreview([]);
      setStemLearningPreview([]);
      setDataAnalyticsAiPreview([]);
      setMilestoneLeaderboardPreview([]);
      setTeamLeaguePreview([]);
      setTournamentPreview([]);
    }
  };

  const loadContestTasks = async (sport: string) => {
    try {
      setLoadingContestTasks(true);
      const response = await fetch(`/api/contest-tasks?sport=${encodeURIComponent(sport)}`);
      const data = await response.json();
      setContestTasks(data?.success && Array.isArray(data?.data) ? data.data : []);
    } catch {
      setContestTasks([]);
    } finally {
      setLoadingContestTasks(false);
    }
  };

  useEffect(() => {
    if (activeModuleId === 'rubric') {
      const framework = sportDisplayName.toLowerCase() === 'badminton' ? badmintonFramework : 'regular';
      const paraClass = sportDisplayName.toLowerCase() === 'badminton' ? badmintonParaClass : 'WH1';
      void loadHomepagePreview(sportDisplayName, framework, paraClass);
      void loadTrainingContentPreview(sportDisplayName);
    }
  }, [activeModuleId, sportDisplayName, badmintonFramework, badmintonParaClass]);

  useEffect(() => {
    if (activeModuleId === 'contests') {
      void loadContestTasks(sportDisplayName);
    }
  }, [activeModuleId, sportDisplayName]);

  // Get unique values for filter dropdowns
  const getUniqueValues = (field: string) => {
    const values = allStrokeTypes
      .map((st: any) => st[field])
      .filter((val: any) => val && val.trim() !== '')
      .filter((val: any, index: number, self: any[]) => self.indexOf(val) === index)
      .sort();
    return values;
  };

  // Filter stroke types based on selected filters
  useEffect(() => {
    let filtered = [...allStrokeTypes];

    // Apply filters
    if (filterCourtPosition) {
      filtered = filtered.filter((st: any) => st.courtPosition === filterCourtPosition);
    }
    if (filterHalfSide) {
      filtered = filtered.filter((st: any) => st.halfSide === filterHalfSide);
    }
    if (filterStrokeType) {
      filtered = filtered.filter((st: any) => st.strokeType === filterStrokeType);
    }
    if (filterPlayType) {
      filtered = filtered.filter((st: any) => st.playType === filterPlayType);
    }
    if (filterDifficulty) {
      // Filter by difficulty - handle both numeric and legacy string values
      filtered = filtered.filter((st: any) => {
        const stDifficulty = normalizeDifficultyValue(st.difficulty);
        const filterDifficultyNum = normalizeDifficultyValue(filterDifficulty);
        return stDifficulty === filterDifficultyNum;
      });
    }
    if (filterShotType) {
      filtered = filtered.filter((st: any) => st.shotType === filterShotType);
    }

    // Show all filtered rows - no pagination
    setStrokeTypes(filtered);
  }, [filterCourtPosition, filterHalfSide, filterStrokeType, filterPlayType, 
      filterShotType, filterDifficulty, allStrokeTypes, isAuthenticated]);

  // Helper function to get court position cell style and color box props
  const getCourtPositionStyle = (position: string) => {
    if (!position) return { style: {}, colorBox: null };
    const pos = position.toLowerCase().trim();
    if (pos.includes('backcourt') || pos.includes('back court')) {
      // Bottom 25% filled
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          bottom: '4px',
          left: '4px',
          right: '4px',
          height: 'calc(25% - 4px)',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
        },
      };
    }
    if (pos.includes('midcourt') || pos.includes('mid court')) {
      // Center filled
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          top: '25%',
          bottom: '25%',
          left: '4px',
          right: '4px',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
        },
      };
    }
    if (pos.includes('frontcourt') || pos.includes('front court') || pos.startsWith('front')) {
      // Top half filled
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
          minHeight: '60px',
        },
        colorBox: {
          position: 'absolute' as const,
          top: '4px',
          left: '4px',
          right: '4px',
          height: 'calc(50% - 4px)',
          minHeight: '24px',
          backgroundColor: '#66bb6a', // More visible green
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
          display: 'block',
        },
      };
    }
    return { style: {}, colorBox: null };
  };

  // Helper function to get color for stroke type
  const getStrokeTypeColor = (strokeType: string) => {
    if (!strokeType) return 'transparent';
    const st = strokeType.toLowerCase();
    // Different colors for different stroke types
    if (st.includes('forehand')) return '#e1f5fe'; // Light blue
    if (st.includes('backhand')) return '#f3e5f5'; // Light purple
    if (st.includes('overhead')) return '#fff3e0'; // Light orange
    if (st.includes('underarm')) return '#e8f5e9'; // Light green
    return '#fafafa'; // Default light gray
  };

  // Helper function to get color for stroke action
  const getStrokeActionColor = (strokeAction: string) => {
    if (!strokeAction) return 'transparent';
    const sa = strokeAction.toLowerCase();
    // Different colors for different stroke actions
    if (sa.includes('overhead')) return '#fff9c4'; // Light yellow
    if (sa.includes('underarm')) return '#e0f2f1'; // Light teal
    if (sa.includes('sidearm')) return '#fce4ec'; // Light pink
    return '#f5f5f5'; // Default light gray
  };

  // Helper function to get color for shot type
  const getShotTypeColor = (shotType: string) => {
    if (!shotType) return 'transparent';
    const st = shotType.toLowerCase();
    // Different colors for different shot types
    const colorMap: { [key: string]: string } = {
      'smash': '#ffebee', // Light red
      'clear': '#e3f2fd', // Light blue
      'drop': '#f1f8e9', // Light green
      'drive': '#fff3e0', // Light orange
      'lift': '#f3e5f5', // Light purple
      'net': '#e0f2f1', // Light teal
      'serve': '#fce4ec', // Light pink
      'kill': '#ffcdd2', // Light red-pink
      'block': '#e1bee7', // Light purple
      'push': '#c5e1a5', // Light green-yellow
    };
    
    for (const [key, color] of Object.entries(colorMap)) {
      if (st.includes(key)) {
        return color;
      }
    }
    return '#fafafa'; // Default light gray
  };

  const getShotTypeTextColor = (shotType: string) => {
    const shot = String(shotType || '').toLowerCase();
    if (shot.includes('smash') || shot.includes('kill')) return '#b71c1c';
    if (shot.includes('drop') || shot.includes('net') || shot.includes('push')) return '#1b5e20';
    if (shot.includes('clear') || shot.includes('lift')) return '#0d47a1';
    if (shot.includes('drive') || shot.includes('serve')) return '#e65100';
    return '#4a148c';
  };

  const getMediaSponsorName = (item: any): string =>
    String(item?.sponsorName || item?.sponsor_name || item?.brandName || item?.sponsor || '').trim();

  const getMediaSponsorLogoUrl = (item: any): string =>
    String(item?.sponsorLogoUrl || item?.sponsorLogo || item?.sponsor_logo_url || item?.logoUrl || '').trim().replace(/\\/g, '/');

  const getStrokeSponsors = (strokeType: any): StrokeSponsor[] => {
    const sponsors = new Map<string, StrokeSponsor>();
    const addSponsor = (item: any) => {
      const sponsorName = getMediaSponsorName(item);
      const sponsorLogoUrl = getMediaSponsorLogoUrl(item);
      if (!sponsorName && !sponsorLogoUrl) return;
      const key = `${sponsorName.toLowerCase()}|${sponsorLogoUrl.toLowerCase()}`;
      sponsors.set(key, { sponsorName, sponsorLogoUrl });
    };
    const addFromArray = (items: unknown) => {
      if (Array.isArray(items)) items.forEach(addSponsor);
    };

    addFromArray(strokeType?.regularTrainingVideos);
    addFromArray(strokeType?.creatorReferenceVideos);
    addFromArray(strokeType?.amateurSampleVideos);
    addFromArray(strokeType?.contestVideos);
    if (Array.isArray(strokeType?.strokeVideoSections)) {
      strokeType.strokeVideoSections.forEach((section: any) => {
        addFromArray(section?.videos);
        addSponsor(section);
      });
    }

    return Array.from(sponsors.values());
  };

  const renderStrokeSponsorLogos = (sponsors: StrokeSponsor[]) => {
    if (sponsors.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, flexWrap: 'wrap' }}>
        {sponsors.map((sponsor, index) => (
          <Box
            key={`${sponsor.sponsorName}-${sponsor.sponsorLogoUrl}-${index}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            title={sponsor.sponsorName || 'Sponsor'}
          >
            {sponsor.sponsorLogoUrl ? (
              <Box
                component="img"
                src={sponsor.sponsorLogoUrl}
                alt={sponsor.sponsorName || 'Sponsor'}
                sx={{
                  width: 40,
                  height: 32,
                  objectFit: 'contain',
                  borderRadius: 1,
                  bgcolor: '#fff',
                  border: '1px solid #e0e7ef',
                  p: 0.25,
                }}
              />
            ) : sponsor.sponsorName ? (
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#5d4037', whiteSpace: 'nowrap' }}>
                {sponsor.sponsorName}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  };

  const renderStrokeSummary = (item: any, compact = false, fullText = false) => {
    const segments = [
      { value: item?.courtPosition, sx: { color: '#0d47a1', fontWeight: 800 } },
      { value: item?.halfSide, sx: { color: '#1565c0', fontWeight: 800 } },
      { value: item?.strokeType, sx: { color: '#263238', fontWeight: 800 } },
      { value: item?.strokeAction, sx: { color: '#374151', fontWeight: 800 } },
      {
        value: item?.shotType,
        sx: {
          color: getShotTypeTextColor(item?.shotType || ''),
          bgcolor: getShotTypeColor(item?.shotType || ''),
          fontWeight: 950,
          px: 0.5,
          borderRadius: 0.75,
        },
      },
      { value: item?.placement, sx: { color: '#4a148c', fontWeight: 800 } },
    ].filter((segment) => Boolean(segment.value));

    if (segments.length === 0) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f4c81' }}>
          {item?.code || 'Stroke'}
        </Typography>
      );
    }

    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant={compact ? 'caption' : 'body2'}
          component="div"
          sx={{
            lineHeight: 1.35,
            color: '#334155',
            whiteSpace: fullText ? 'normal' : 'nowrap',
            overflow: fullText ? 'visible' : 'hidden',
            textOverflow: fullText ? 'clip' : 'ellipsis',
            wordBreak: fullText ? 'break-word' : 'normal',
          }}
        >
          {segments.map((segment, index) => (
            <React.Fragment key={`${item?.id || item?.code}-${segment.value}-${index}`}>
              {index > 0 && (
                <Box component="span" sx={{ mx: 0.55, color: '#94a3b8', fontWeight: 700 }}>
                  |
                </Box>
              )}
              <Box component="span" sx={segment.sx}>
                {segment.value}
              </Box>
            </React.Fragment>
          ))}
        </Typography>
        {item?.code && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right', mt: 0.15, fontSize: '0.62rem', fontWeight: 800 }}
          >
            {item.code}
          </Typography>
        )}
      </Box>
    );
  };

  // Helper function to get half side cell style and color box props (green like badminton court)
  const getHalfSideStyle = (halfSide: string) => {
    if (!halfSide) return { style: {}, colorBox: null };
    const side = halfSide.toLowerCase();
    if (side.includes('right')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          right: '4px',
          top: '4px',
          bottom: '4px',
          width: 'calc(50% - 4px)',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    if (side.includes('left')) {
      return {
        style: {
          position: 'relative' as const,
          padding: '8px',
        },
        colorBox: {
          position: 'absolute' as const,
          left: '4px',
          top: '4px',
          bottom: '4px',
          width: 'calc(50% - 4px)',
          backgroundColor: '#66bb6a',
          borderRadius: '2px',
          zIndex: 0,
          pointerEvents: 'none' as const,
        },
      };
    }
    return { style: {}, colorBox: null };
  };

  // State for rankings data
  const [allRankings, setAllRankings] = useState<{
    selfEvaluation: any[];
    coachAcademy: any[];
    tournament: any[];
    practicalTest: any[];
  }>({
    selfEvaluation: [],
    coachAcademy: [],
    tournament: [],
    practicalTest: [],
  });
  const [loadingRankings, setLoadingRankings] = useState(false);

  // Load rankings from API (when Ranking tab is active).
  useEffect(() => {
    if (activeModuleId === 'ranking') {
      loadRankings();
    }
  }, [rankingTypeTab, rankingPeriodKey, sportDisplayName, activeModuleId]);

  const loadRankings = async () => {
    setLoadingRankings(true);
    try {
      // Map tab index to category ID (Self Evaluation and Tournament hidden for public)
      const categoryMap: { [key: number]: string } = {
        0: 'coach-academy',
        1: 'practical-test',
      };

      const categoryId = categoryMap[rankingTypeTab];
      
      if (!categoryId) {
        setLoadingRankings(false);
        return;
      }

      if (!categoryId) {
        setLoadingRankings(false);
        return;
      }

      console.log(`[Ranking] Loading v2 rankings for sport: ${sportDisplayName}, category: ${categoryId}, periodKey: ${rankingPeriodKey || '(latest)'} tab: ${rankingTypeTab}`);
      const qs = new URLSearchParams();
      qs.set('periodType', 'yearly');
      if (rankingPeriodKey) qs.set('periodKey', rankingPeriodKey);
      const response = await fetch(`/api/ranking-reports/leaderboard/${encodeURIComponent(sportDisplayName)}/${encodeURIComponent(categoryId)}?${qs.toString()}`);
      if (response.ok) {
        const data = await response.json();
        console.log(`[Ranking v2] API response:`, { success: data.success, count: data.data?.length, meta: data.meta });
        if (data.success && Array.isArray(data.data)) {
          // If v2 has no data yet, fall back to legacy endpoint to avoid breaking public rankings during transition.
          if (data.data.length === 0) {
            console.warn('[Ranking v2] No v2 data yet, falling back to legacy all-players endpoint');
            const legacyRes = await fetch(`/api/ranking-evaluations/all-players/${categoryId}`);
            if (legacyRes.ok) {
              const legacy = await legacyRes.json();
              if (legacy.success && Array.isArray(legacy.data)) {
                const legacyRankings = legacy.data.map((player: any, index: number) => ({
                  rank: index + 1,
                  playerName: player.playerName || 'Unknown',
                  score: parseFloat(player.score) || 0,
                  ageCategory: player.ageCategory || '',
                  academy: player.academy || '',
                  country: player.country || '',
                  state: player.state || '',
                  year: player.evaluationDate ? new Date(player.evaluationDate).getFullYear().toString() : new Date().getFullYear().toString(),
                  gender: 'All',
                  type: rankingTypeTab === 0 ? 'Coach/Academy Evaluation' : 'Board Exam',
                  backcourtPercentage: parseFloat(player.backcourtPercentage) || 0,
                  midcourtPercentage: parseFloat(player.midcourtPercentage) || 0,
                  frontcourtPercentage: parseFloat(player.frontcourtPercentage) || 0,
                }));
                if (rankingTypeTab === 0) setAllRankings(prev => ({ ...prev, coachAcademy: legacyRankings }));
                else if (rankingTypeTab === 1) setAllRankings(prev => ({ ...prev, practicalTest: legacyRankings }));
                setLoadingRankings(false);
                return;
              }
            }
          }
          const rankings = data.data.map((player: any, index: number) => ({
            rank: index + 1,
            playerName: player.playerName || 'Unknown',
            score: parseFloat(player.score) || 0,
            ageCategory: player.ageCategory || '',
            academy: player.academy || '',
            school: player.school || '',
            country: player.country || '',
            state: player.state || '',
            year: player.periodKey ? String(player.periodKey).split('-')[1] : (player.periodEndDate ? new Date(player.periodEndDate).getFullYear().toString() : new Date().getFullYear().toString()),
            gender: 'All',
            type: rankingTypeTab === 0 ? 'Coach/Academy Evaluation' : 'Board Exam',
            backcourtPercentage: parseFloat(player.backcourtPercentage) || 0,
            midcourtPercentage: parseFloat(player.midcourtPercentage) || 0,
            frontcourtPercentage: parseFloat(player.frontcourtPercentage) || 0,
            periodKey: player.periodKey,
            reportId: player.reportId,
          }));
          
          console.log(`[Ranking v2] Transformed ${rankings.length} rankings. Sample:`, rankings.slice(0, 3).map(r => r.playerName));

          // Update the appropriate category
          if (rankingTypeTab === 0) {
            setAllRankings(prev => ({ ...prev, coachAcademy: rankings }));
          } else if (rankingTypeTab === 1) {
            setAllRankings(prev => ({ ...prev, practicalTest: rankings }));
          }
        } else {
          console.warn(`[Ranking v2] No data returned for category ${categoryId}`);
          // Set empty array if no data
          if (rankingTypeTab === 0) {
            setAllRankings(prev => ({ ...prev, coachAcademy: [] }));
          } else if (rankingTypeTab === 1) {
            setAllRankings(prev => ({ ...prev, practicalTest: [] }));
          }
        }
      } else {
        const errorText = await response.text();
        console.error(`[Ranking v2] Failed to load rankings: ${response.status} - ${errorText}`);
        // Set empty array on error
        if (rankingTypeTab === 0) {
          setAllRankings(prev => ({ ...prev, coachAcademy: [] }));
        } else if (rankingTypeTab === 1) {
          setAllRankings(prev => ({ ...prev, practicalTest: [] }));
        }
      }
    } catch (err) {
      console.error('Error loading rankings:', err);
    } finally {
      setLoadingRankings(false);
    }
  };

  // Get rankings based on selected tab (Self Evaluation and Tournament hidden for public)
  const getRankingsForType = () => {
    if (rankingTypeTab === 0) return allRankings.coachAcademy;
    if (rankingTypeTab === 1) return allRankings.practicalTest;
    return [];
  };

  // Filter rankings based on ranking level, country, state, age category, year, gender, and player name
  const currentRankings = getRankingsForType();
  console.log(`[Ranking Table] Current rankings for tab ${rankingTypeTab}:`, currentRankings.length, currentRankings);
  const filteredRankings = currentRankings.filter((player) => {
    // Filter by ranking level (national/state)
    if (rankingLevel === 'national') {
      if (selectedCountry && player.country !== selectedCountry) return false;
    } else if (rankingLevel === 'state') {
      if (selectedCountry && player.country !== selectedCountry) return false;
      if (selectedState && player.state !== selectedState) return false;
    }
    
    // Filter by age category
    if (selectedAgeCategory && player.ageCategory !== selectedAgeCategory) return false;
    
    // Filter by year
    if (selectedYear && player.year !== selectedYear) return false;
    
    // Filter by gender
    if (selectedGender && selectedGender !== 'All' && player.gender !== selectedGender) return false;
    
    // Filter by player name search
    if (playerNameSearch && !player.playerName.toLowerCase().includes(playerNameSearch.toLowerCase())) return false;
    
    return true;
  });

  // Re-rank filtered results
  const mockRankings = filteredRankings.map((player, index) => ({
    ...player,
    rank: index + 1,
  }));
  
  console.log(`[Ranking Table] Filtered rankings: ${filteredRankings.length}, Final mockRankings: ${mockRankings.length}`);
  if (mockRankings.length > 0) {
    console.log(`[Ranking Table] First player in table:`, mockRankings[0]);
  }

  // Helper function to remove filter
  const removeFilter = (filterType: string) => {
    switch (filterType) {
      case 'country':
        setSelectedCountry('');
        setSelectedState('');
        break;
      case 'state':
        setSelectedState('');
        break;
      case 'ageCategory':
        setSelectedAgeCategory('');
        break;
      case 'year':
        setSelectedYear('');
        break;
      case 'gender':
        setSelectedGender('');
        break;
      case 'playerName':
        setPlayerNameSearch('');
        break;
      case 'rankingLevel':
        setRankingLevel('national');
        setSelectedState('');
        break;
    }
  };

  // Mock data for tournaments
  const mockTournaments = [
    {
      id: 1,
      name: `${sportDisplayName} Championship 2024`,
      date: '2024-03-15',
      location: 'National Sports Complex',
      status: 'Upcoming',
      category: 'U15',
    },
    {
      id: 2,
      name: `${sportDisplayName} Open Tournament`,
      date: '2024-04-20',
      location: 'City Arena',
      status: 'Upcoming',
      category: 'U17',
    },
    {
      id: 3,
      name: `${sportDisplayName} Regional Championship`,
      date: '2024-02-10',
      location: 'Regional Sports Center',
      status: 'Completed',
      category: 'U15',
    },
  ];

  const contestRegistrationText = CONTEST_REGISTRATION_TEXT;

  const contestActionPath = (taskId?: string, view?: 'submit' | 'results' | 'leaderboard') => {
    const viewMap: Record<string, string> = {
      submit: 'browse',
      results: 'submissions',
      leaderboard: 'leaderboard',
    };
    const params = new URLSearchParams();
    const mappedView = view ? viewMap[view] : '';
    if (mappedView && mappedView !== 'browse') params.set('view', mappedView);
    if (taskId) params.set('taskId', taskId);
    const slug = sportDisplayName.trim().toLowerCase().replace(/\s+/g, '-');
    const query = params.toString();
    return `/player/contests/${encodeURIComponent(slug)}/all${query ? `?${query}` : ''}`;
  };

  const renderContestTaskCard = (task: PublicContestTask) => (
    <ContestTaskCard
      key={task.id}
      task={task}
      onViewResults={() => navigate(contestActionPath(task.id, 'results'))}
      onViewLeaderboard={() => navigate(contestActionPath(task.id, 'leaderboard'))}
      onSubmit={() => navigate(isAuthenticated ? contestActionPath(task.id, 'submit') : '/login')}
      submitLabel={isAuthenticated ? 'Submit Performance' : 'Login to Submit'}
    />
  );

  const renderFeaturedSmashContest = () => (
    <FeaturedSmashContest
      sportName={sportDisplayName}
      onViewResults={() => navigate(contestActionPath(undefined, 'results'))}
      onViewLeaderboard={() => navigate(contestActionPath(undefined, 'leaderboard'))}
      onSubmit={() => navigate(isAuthenticated ? contestActionPath(undefined, 'submit') : '/login')}
      submitLabel={isAuthenticated ? 'Submit Your Video' : 'Login to Submit'}
    />
  );

  function renderRubricAccordion(
    title: string,
    countLabel: string,
    children: React.ReactNode,
    headerBg = '#e3f2fd',
    borderColor = '#bbdefb'
  ) {
    return (
      <Accordion
        disableGutters
        sx={{
          border: '1px solid #dbe7f3',
          borderRadius: '12px !important',
          bgcolor: 'rgba(255,255,255,0.75)',
          boxShadow: 'none',
          overflow: 'hidden',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#0f4c81' }} />}
          sx={{
            bgcolor: headerBg,
            borderBottom: `1px solid ${borderColor}`,
            minHeight: 46,
            '& .MuiAccordionSummary-content': { my: 0.75 },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f4c81' }}>
            {title} ({countLabel})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 1.5 }}>
          {children}
        </AccordionDetails>
      </Accordion>
    );
  }

  const renderAthleteNutritionChart = () => {
    const nutritionRows = [
      {
        ageGroup: 'U9 (6-8)',
        dailyIntake: '3 meals + 2 snacks; water 1.2-1.6 L + training sips',
        growthFuel: 'Milk/curd, dal, egg/paneer, rice/chapati, fruits, vegetables',
        trainingTiming: 'Banana or milk before practice; milk/curd + carb after practice',
        athleteFocus: 'Healthy growth, bone strength, steady energy, habit building',
      },
      {
        ageGroup: 'U11-U13 (9-12)',
        dailyIntake: '3 meals + 2-3 snacks; protein at each meal; water 1.5-2.0 L + session fluids',
        growthFuel: 'Dal/beans, eggs, fish/chicken/paneer, nuts, curd, seasonal fruit, vegetables',
        trainingTiming: 'Carb snack 30-60 min before; carb + protein within 60 min after training',
        athleteFocus: 'Growth spurt support, recovery, immunity, concentration for school + sport',
      },
      {
        ageGroup: 'U15 (13-15)',
        dailyIntake: 'Higher calorie athlete plate; 1.2-1.6 g/kg/day protein; water 2.0-2.5 L + sweat replacement',
        growthFuel: 'Rice/chapati/pasta, lentils, dairy, eggs/lean meat, nuts/seeds, 4-5 fruit/veg servings',
        trainingTiming: 'Pre-practice carb; post-practice meal with protein, carb, fluids, and salt as needed',
        athleteFocus: 'Peak growth, lean muscle, menstrual health for girls, bone loading tolerance',
      },
      {
        ageGroup: 'U17-U19 (16-19)',
        dailyIntake: 'Performance plate by load; 1.4-1.8 g/kg/day protein; carbs increased on heavy days',
        growthFuel: 'Whole grains, lean protein, dairy/fortified calcium, iron-rich foods, fruit/vegetables',
        trainingTiming: 'Fuel before every intense session; recovery meal within 30-60 min; hydrate by sweat loss',
        athleteFocus: 'Power, repeated sprint ability, recovery between sessions, injury-risk reduction',
      },
      {
        ageGroup: 'Adult / Pro pathway',
        dailyIntake: 'Periodized intake by training block; protein 1.6-2.0 g/kg/day; planned electrolytes',
        growthFuel: 'Sport-specific carb loading/recovery, lean protein, anti-inflammatory foods, micronutrients',
        trainingTiming: 'Pre-match meal 3-4h before; snack 30-60 min before; structured post-match recovery',
        athleteFocus: 'Match readiness, body composition, recovery speed, consistency across tournaments',
      },
    ];

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Athlete nutrition is not a regular diet chart. Intake must support growth, school load, training energy,
          muscle repair, bone strength, hydration, and recovery. Final plans should be adjusted by parent, coach,
          pediatrician, or sports dietitian based on body weight, growth trend, training hours, sweat rate, and food preference.
        </Typography>
        <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#ecfdf5' }}>
                <TableCell sx={{ fontWeight: 800 }}>Age Group</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Daily Athlete Intake</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Growth Foods</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Training Timing</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Athlete Focus</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nutritionRows.map((row, index) => (
                <TableRow key={row.ageGroup} sx={{ bgcolor: index % 2 === 0 ? '#fcfffd' : '#f6fef9' }}>
                  <TableCell sx={{ fontWeight: 800, color: '#166534', minWidth: 120 }}>{row.ageGroup}</TableCell>
                  <TableCell>{row.dailyIntake}</TableCell>
                  <TableCell>{row.growthFuel}</TableCell>
                  <TableCell>{row.trainingTiming}</TableCell>
                  <TableCell>{row.athleteFocus}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderLearningStrokeRow = (strokeType: any) => {
    const difficultyValue = normalizeDifficultyValue(strokeType.difficulty);
    const videoUrls = String(strokeType.referenceVideos || '')
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);
    const sponsors = getStrokeSponsors(strokeType);

    return (
      <Box
        key={strokeType.id || strokeType.code}
        sx={{
          border: '1px solid #dbe8f6',
          borderLeft: '4px solid #4caf50',
          borderRadius: 1,
          px: 1.25,
          py: 0.85,
          bgcolor: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {renderStrokeSummary(strokeType, false, true)}
          </Box>
          {(strokeType.playType || difficultyValue > 0) && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flexShrink: 0 }}>
              {strokeType.playType && (
                <Chip size="small" label={`${strokeTableLabels.playType}: ${strokeType.playType}`} variant="outlined" />
              )}
              {difficultyValue > 0 && (
                <Chip size="small" label={`${difficultyValue}/5`} color="primary" variant="outlined" />
              )}
            </Box>
          )}
          {renderStrokeSponsorLogos(sponsors)}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexShrink: 0,
              alignItems: 'center',
              pl: { md: sponsors.length > 0 || videoUrls.length > 0 ? 1.5 : 0 },
              ml: { md: 'auto' },
              borderLeft: { md: sponsors.length > 0 || videoUrls.length > 0 ? '1px solid #e2e8f0' : 'none' },
            }}
          >
            {videoUrls.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>No video</Typography>
            ) : (
              videoUrls.slice(0, 3).map((url, idx) => (
                <VideoPlayer key={`${url}-${idx}`} url={url} iconOnly />
              ))
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Learning content sections - Hidden for now, will be implemented later
  // const learningSections = [
  //   {
  //     title: 'Basic Techniques',
  //     content: `Master the fundamental techniques of ${sportDisplayName}. Learn proper grip, stance, and basic strokes that form the foundation of your game.`,
  //     icon: <SchoolIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
  //   },
  //   {
  //     title: 'Advanced Strategies',
  //     content: `Explore advanced strategies and tactics used by professional players. Understand positioning, shot selection, and game planning.`,
  //     icon: <EmojiEventsIcon sx={{ fontSize: 40, color: '#d32f2f' }} />,
  //   },
  // ];

  const sportTabStyles = [
    {
      color: '#0f4c81',
      border: '#bfdbfe',
      gradient: 'linear-gradient(135deg, #e0f2fe 0%, #eff6ff 52%, #ffffff 100%)',
      active: 'linear-gradient(135deg, #0f4c81 0%, #1976d2 55%, #42a5f5 100%)',
      glow: 'rgba(25, 118, 210, 0.28)',
    },
    {
      color: '#166534',
      border: '#bbf7d0',
      gradient: 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 52%, #ffffff 100%)',
      active: 'linear-gradient(135deg, #166534 0%, #22c55e 58%, #86efac 100%)',
      glow: 'rgba(34, 197, 94, 0.28)',
    },
    {
      color: '#92400e',
      border: '#fed7aa',
      gradient: 'linear-gradient(135deg, #ffedd5 0%, #fff7ed 52%, #ffffff 100%)',
      active: 'linear-gradient(135deg, #b45309 0%, #f59e0b 58%, #fcd34d 100%)',
      glow: 'rgba(245, 158, 11, 0.3)',
    },
    {
      color: '#6b21a8',
      border: '#e9d5ff',
      gradient: 'linear-gradient(135deg, #f3e8ff 0%, #faf5ff 52%, #ffffff 100%)',
      active: 'linear-gradient(135deg, #6b21a8 0%, #a855f7 58%, #d8b4fe 100%)',
      glow: 'rgba(168, 85, 247, 0.3)',
    },
  ];

  const getSportTabSx = (index: number) => {
    const style = sportTabStyles[index];
    return {
      minHeight: 52,
      borderRadius: 2,
      mx: 0.5,
      px: { xs: 1.25, sm: 2 },
      textTransform: 'none',
      fontWeight: 800,
      color: style.color,
      border: `1px solid ${style.border}`,
      background: style.gradient,
      boxShadow: `0 8px 20px ${style.glow}`,
      overflow: 'hidden',
      position: 'relative',
      transition: 'all 0.2s ease',
      '&:before': {
        content: '""',
        position: 'absolute',
        top: -24,
        right: -20,
        width: 74,
        height: 74,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.38)',
      },
      '&:after': {
        content: '""',
        position: 'absolute',
        bottom: -30,
        left: -18,
        width: 88,
        height: 88,
        borderRadius: '35%',
        transform: 'rotate(18deg)',
        background: 'rgba(255,255,255,0.24)',
      },
      '& .MuiTab-iconWrapper': {
        zIndex: 1,
        mr: 0.75,
      },
      '& .MuiTab-wrapper, & .MuiSvgIcon-root': {
        zIndex: 1,
      },
      '&.Mui-selected': {
        color: '#ffffff',
        background: style.active,
        borderColor: 'transparent',
        boxShadow: `0 12px 26px ${style.glow}`,
      },
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 14px 28px ${style.glow}`,
      },
    };
  };

  return (
    <Box>
      {/* Navigation Bar */}
      <AppBar position="sticky" sx={{ bgcolor: '#ffffff', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <EncycdataLogo height={20} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1f2937',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              EncycGames
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
              {CORE_SPORTS.map((sport) => (
                <Button
                  key={sport}
                  variant="text"
                  size="small"
                  onClick={() => navigate(`/sport/${toSportSlug(sport)}`)}
                  sx={{ textTransform: 'none', color: sportDisplayName === sport ? '#1976d2' : '#1f2937', fontWeight: sportDisplayName === sport ? 700 : 500 }}
                >
                  {sport}
                </Button>
              ))}
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
            }}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Tabs Section */}
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
          {sportDisplayName}
        </Typography>
        <Box sx={{ borderBottom: 0, mb: 1 }}>
          <Tabs
            value={Math.min(tabValue, Math.max(visibleSportTabs.length - 1, 0))}
            onChange={handleTabChange}
            aria-label="sport page tabs"
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            sx={{
              minHeight: 62,
              alignItems: 'center',
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0.5, sm: 1 },
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            {visibleSportTabs.map((tab, index) => (
              <Tab
                key={tab.id}
                icon={sportTabIconByModule[tab.id]}
                iconPosition="start"
                label={tab.label}
                id={`sport-tab-${tab.id}`}
                aria-controls={`sport-tabpanel-${tab.id}`}
                sx={getSportTabSx(index)}
              />
            ))}
          </Tabs>
        </Box>

        {visibleSportTabs.length === 0 && (
          <Alert severity="info" sx={{ my: 3 }}>
            Modules for {sportDisplayName} are not published yet. Check back soon.
          </Alert>
        )}

        {/* Rubric Tab */}
        {activeModuleId === 'rubric' && (
        <TabPanel value={0} index={0}>
          {!sportModuleEnabled('rubric') ? (
            <SportModuleComingSoon
              sport={sportDisplayName}
              moduleLabel="Sport Rubric"
              state={sportModuleState('rubric')}
              notes={sportModuleMatrix?.matrix?.[sportDisplayName]?.rubric?.visibilityNotes}
            />
          ) : (
          <>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
            Long Term Player Development and Performance Blueprint and Rubric
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            A complete structured learning and performance evaluation system for {sportDisplayName} that measures skills, drills, and fitness.
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={rubricTabValue}
              onChange={(_, value) => setRubricTabValue(value)}
              aria-label="rubric sub-tabs"
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                minHeight: 42,
                '& .MuiTab-root': {
                  minHeight: 42,
                  textTransform: 'none',
                  fontWeight: 800,
                },
              }}
            >
              <Tab label="Athlete Development" />
              <Tab label="Assessment" />
            </Tabs>
          </Box>

          {rubricTabValue === 0 && (
            <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              {renderRubricAccordion('Core Skills', '6', (
              <Box sx={{ border: '1px solid #dbe7f3', borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.75)', p: 1.5, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f4c81', mb: 1 }}>
                  Core Skills
                </Typography>
                {isIceHockeySport ? (
                  <>
                    {[
                      { label: 'Skating', value: '28+' },
                      { label: 'Puck Skills', value: '34+' },
                      { label: 'Shooting', value: '26+' },
                      { label: 'Hockey Skills', value: '22+' },
                      { label: 'Fitness', value: fitnessLabel },
                    ].map((row) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' },
                          alignItems: 'center',
                          columnGap: 1.5,
                          mb: 0.75,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>{row.label}</Typography>
                        <Typography variant="body2" sx={{ color: row.label === 'Fitness' ? '#1b6b5b' : '#0f4c81', fontWeight: 800 }}>
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Drills can be skill-specific (e.g. skating only) or mixed-skill combinations (e.g. skating + puck + shooting).
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>{coreSkillLabel}</Typography>
                    <Typography variant="body2" sx={{ color: '#0f4c81', fontWeight: 800 }}>{strokesLabel}</Typography>
                  </Box>
                )}
                {isFootballSport && (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Passing & Receiving</Typography>
                      <Typography variant="body2" sx={{ color: '#0f4c81', fontWeight: 800 }}>{footballCoreSkillCounts.passingReceiving}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Dribbling</Typography>
                      <Typography variant="body2" sx={{ color: '#0f4c81', fontWeight: 800 }}>{footballCoreSkillCounts.dribbling}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Heading</Typography>
                      <Typography variant="body2" sx={{ color: '#0f4c81', fontWeight: 800 }}>{footballCoreSkillCounts.heading}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Turning</Typography>
                      <Typography variant="body2" sx={{ color: '#0f4c81', fontWeight: 800 }}>{footballCoreSkillCounts.turning}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Goalkeeper</Typography>
                      <Typography variant="body2" sx={{ color: '#0f4c81', fontWeight: 800 }}>{footballCoreSkillCounts.goalkeeper}</Typography>
                    </Box>
                  </>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Drills</Typography>
                  <Typography variant="body2" sx={{ color: '#245c9f', fontWeight: 800 }}>{drillsLabel}</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Fitness</Typography>
                  <Typography variant="body2" sx={{ color: '#1b6b5b', fontWeight: 800 }}>{fitnessLabel}</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Time to Pro Level</Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 800 }}>4-8+ years</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5, mb: 0.75 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Daily Practice Hours</Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 800 }}>2-6+ hours</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '240px 1fr' }, alignItems: 'center', columnGap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 700 }}>Total Cost to Become Pro</Typography>
                  <Typography variant="body2" sx={{ color: '#111827', fontWeight: 800 }}>INR 6-15 lacs</Typography>
                </Box>
              </Box>
              ), '#e3f2fd', '#bbdefb')}
            </Grid>
          </Grid>

          {isFootballSport && (
            <Box sx={{ border: '1px solid #dbe7f3', borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.75)', p: 1.5, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f4c81', mb: 1 }}>
                Role-wise Football Development
              </Typography>
              <Tabs
                value={footballRoleTab}
                onChange={(_, value) => setFootballRoleTab(value)}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  mb: 1.5,
                  '& .MuiTabs-flexContainer': { gap: 1 },
                  '& .MuiTab-root': {
                    minHeight: 36,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.5,
                  },
                  '& .MuiTabs-indicator': { display: 'none' },
                }}
              >
                <Tab value="skills" label="Skills" sx={{ bgcolor: footballRoleTab === 'skills' ? '#dbeafe' : '#eef4ff', color: footballRoleTab === 'skills' ? '#0f4c81' : '#415a77' }} />
                <Tab value="single-drills" label="Single-skill Drills" sx={{ bgcolor: footballRoleTab === 'single-drills' ? '#dcfce7' : '#effaf2', color: footballRoleTab === 'single-drills' ? '#166534' : '#3f5f4b' }} />
                <Tab value="mixed-drills" label="Mixed-skill Drills" sx={{ bgcolor: footballRoleTab === 'mixed-drills' ? '#fff4e5' : '#fff8ef', color: footballRoleTab === 'mixed-drills' ? '#b45309' : '#7c5b2e' }} />
                <Tab value="fitness" label="Fitness" sx={{ bgcolor: footballRoleTab === 'fitness' ? '#f3e8ff' : '#faf4ff', color: footballRoleTab === 'fitness' ? '#6b21a8' : '#5b4b72' }} />
              </Tabs>

              {footballRoleTab === 'skills' && (
                <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#eff6ff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Role / Position</TableCell>
                        {footballSkillColumns.map((column) => (
                          <TableCell key={column} sx={{ fontWeight: 700 }}>{column}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {footballRoleSkillRows.map((row, index) => (
                        <TableRow key={row.role} sx={{ bgcolor: index % 2 === 0 ? '#fcfdff' : '#f8fbff' }}>
                          <TableCell sx={{ fontWeight: 600 }}>{row.role}</TableCell>
                          <TableCell>{row.shooting}</TableCell>
                          <TableCell>{row.dribbling}</TableCell>
                          <TableCell>{row.passingReceiving}</TableCell>
                          <TableCell>{row.heading}</TableCell>
                          <TableCell>{row.turning}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {footballRoleTab === 'single-drills' && (
                <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#ecfdf5' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Role / Position</TableCell>
                        {footballSkillColumns.map((column) => (
                          <TableCell key={column} sx={{ fontWeight: 700 }}>{column}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {footballRoleSingleSkillDrillRows.map((row, index) => (
                        <TableRow key={row.role} sx={{ bgcolor: index % 2 === 0 ? '#fcfffd' : '#f5fdf8' }}>
                          <TableCell sx={{ fontWeight: 600 }}>{row.role}</TableCell>
                          <TableCell>{row.shooting}</TableCell>
                          <TableCell>{row.dribbling}</TableCell>
                          <TableCell>{row.passingReceiving}</TableCell>
                          <TableCell>{row.heading}</TableCell>
                          <TableCell>{row.turning}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {footballRoleTab === 'mixed-drills' && (
                <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fff7ed' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Role / Position</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {footballRoleMixedSkillDrillRows.map((row, index) => (
                        <TableRow key={row.role} sx={{ bgcolor: index % 2 === 0 ? '#fffdfa' : '#fff8f1' }}>
                          <TableCell sx={{ fontWeight: 600 }}>{row.role}</TableCell>
                          <TableCell>{row.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {footballRoleTab === 'fitness' && (
                <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#faf5ff' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Role / Position</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {footballRoleFitnessRows.map((row, index) => (
                        <TableRow key={row.role} sx={{ bgcolor: index % 2 === 0 ? '#fdfbff' : '#faf7ff' }}>
                          <TableCell sx={{ fontWeight: 600 }}>{row.role}</TableCell>
                          <TableCell>{row.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {renderRubricAccordion(
            `Master your ${sportDisplayName} Journey`,
            `${cardHomePreview.strokes.length + cardHomePreview.drills.length + cardFitnessPreviewItems.length}`,
            (
          <Box sx={{ border: '1px solid #dbe7f3', borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.75)', p: 1.5, mb: 2 }}>
            <Box sx={{ mb: 1 }}>
                <Tabs
                  value={journeyTab}
                  onChange={(_, value) => setJourneyTab(value)}
                  sx={{
                    minHeight: 36,
                    mb: 1,
                    '& .MuiTabs-flexContainer': { gap: 1 },
                    '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700, borderRadius: 1, px: 1.25, py: 0.5 },
                    '& .MuiTabs-indicator': { display: 'none' },
                  }}
                >
                  <Tab value="strokes" label={`${coreSkillLabel} (${strokesLabel})`} sx={{ bgcolor: journeyTab === 'strokes' ? '#dbeafe' : '#eef4ff' }} />
                  <Tab value="drills" label={`Drills (${drillsLabel})`} sx={{ bgcolor: journeyTab === 'drills' ? '#dcfce7' : '#effaf2' }} />
                  <Tab value="fitness" label={`Fitness (${fitnessLabel})`} sx={{ bgcolor: journeyTab === 'fitness' ? '#f3e8ff' : '#faf4ff' }} />
                </Tabs>
                {journeyTab === 'strokes' && (
                  cardHomePreview.strokes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No stroke preview marked yet in admin.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {cardHomePreview.strokes.slice(0, 10).map((item: any) => (
                        <Box
                          key={item.id}
                          sx={{
                            border: '1px solid #dbe8f6',
                            borderLeft: '4px solid #4caf50',
                            borderRadius: 1,
                            px: 1,
                            py: 0.6,
                            bgcolor: '#ffffff',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {renderStrokeSummary(item, true, true)}
                            </Box>
                            {renderStrokeSponsorLogos(getStrokeSponsors(item))}
                            {Array.isArray(item.referenceVideos) && item.referenceVideos.length > 0 ? (
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, pl: 1.5, borderLeft: '1px solid #e2e8f0' }}>
                                {item.referenceVideos.slice(0, 3).map((videoUrl: string, idx: number) => (
                                  <VideoPlayer key={`${item.id}-video-${idx}`} url={videoUrl} iconOnly />
                                ))}
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )
                )}
                {journeyTab === 'drills' && (
                  cardHomePreview.drills.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No drill preview marked yet in admin.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {cardHomePreview.drills.slice(0, 10).map((item: any) => (
                        <Box
                          key={item.id}
                          sx={{
                            border: '1px solid #dbe8f6',
                            borderLeft: '4px solid #4caf50',
                            borderRadius: 1,
                            px: 1,
                            py: 0.6,
                            bgcolor: '#ffffff',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', flexWrap: 'nowrap' }}>
                            {[
                              { value: item.drillName, sx: { fontWeight: 700 } },
                              { value: item.drillType, sx: { color: '#0d47a1', fontWeight: 600 } },
                              { value: item.courtPosition, sx: { color: '#4a148c', fontWeight: 600 } },
                            ]
                              .filter((segment) => Boolean(segment.value))
                              .map((segment, idx) => (
                                <React.Fragment key={`${item.id}-drill-seg-${idx}`}>
                                  {idx > 0 && <Typography variant="caption" color="text.secondary">|</Typography>}
                                  <Typography variant="caption" sx={segment.sx}>{segment.value}</Typography>
                                </React.Fragment>
                              ))}
                            {Array.isArray(item.referenceVideos) && item.referenceVideos.length > 0 ? (
                              <>
                                <Typography variant="caption" color="text.secondary">|</Typography>
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0, pl: 1.5 }}>
                                  <VideoPlayer url={item.referenceVideos[0]} iconOnly />
                                </Box>
                              </>
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )
                )}
                {journeyTab === 'fitness' && (
                  cardFitnessPreviewItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No fitness preview marked yet in admin.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {cardFitnessPreviewItems.slice(0, 10).map((item: any) => (
                        <Box
                          key={item.id}
                          sx={{
                            border: '1px solid #dbe8f6',
                            borderLeft: '4px solid #4caf50',
                            borderRadius: 1,
                            px: 1,
                            py: 0.6,
                            bgcolor: '#ffffff',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'center', flexWrap: 'nowrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>{item.itemName}</Typography>
                            <Typography variant="caption" color="text.secondary">|</Typography>
                            <Typography variant="caption" sx={{ color: '#0d47a1', fontWeight: 600 }}>{item.category}</Typography>
                            {Array.isArray(item.referenceVideos) && item.referenceVideos.length > 0 ? (
                              <>
                                <Typography variant="caption" color="text.secondary">|</Typography>
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0, pl: 1.5 }}>
                                  <VideoPlayer url={item.referenceVideos[0]} iconOnly />
                                </Box>
                              </>
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )
                )}
          </Box>
          </Box>
            ),
            '#eef4ff',
            '#c8dafc'
          )}

          <Box sx={{ mb: 2 }}>
            {renderRubricAccordion(
              'Talent Score Card - Measure like an Academic',
              '8',
              <TalentScoreCardTable coreSkillLabel={coreSkillLabel} />,
              '#f3e8ff',
              '#d1c4e9'
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            {renderRubricAccordion(
              'Nutrition Chart',
              '5 age groups',
              renderAthleteNutritionChart(),
              '#ecfdf5',
              '#bbf7d0'
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            {renderRubricAccordion('Physical Benchmark', isBadmintonSport ? '3' : isIceHockeySport ? '4' : '4', (
            <>
            {isBadmintonSport ? (
              <BadmintonEnglandPhysicalBenchmarkTable />
            ) : isIceHockeySport ? (
              <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Development-pathway physical screening bands by age. Federation and combine protocols set final eligibility; numbers below are illustrative targets for academy tracking.
              </Typography>
              <IceHockeyPhysicalBenchmarkTable />
              </>
            ) : (
              <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Country-wise physical benchmark references for eligibility into national and international tournaments. These standards vary by federation and competition level.
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
                <Chip size="small" label="National Eligibility" variant="outlined" />
                <Chip size="small" label="International Pathway" variant="outlined" />
                <Chip size="small" label="Country Specific" variant="outlined" />
              </Box>
              <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7fbff' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Country / Federation</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Eligibility Focus</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Physical Benchmark Examples</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { country: 'India', focus: 'National + International', benchmark: 'Endurance, speed, agility, lower-body power' },
                      { country: 'Indonesia', focus: 'National Circuit', benchmark: 'Court movement, recovery capacity, match endurance' },
                      { country: 'Denmark', focus: 'Elite Development', benchmark: 'Explosive movement, aerobic base, consistency tests' },
                      { country: 'USA', focus: 'Tournament Qualification', benchmark: 'Strength, movement quality, performance readiness' },
                    ].map((row) => (
                      <TableRow key={row.country}>
                        <TableCell>{row.country}</TableCell>
                        <TableCell>{row.focus}</TableCell>
                        <TableCell>{row.benchmark}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
            )}
            </>
            ), '#fff8e1', '#ffe082')}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {renderRubricAccordion('Coaching Pedagogy', '12+', <CoachingPedagogyTable />, '#e3f2fd', '#bbdefb')}
            {renderRubricAccordion('Training Methods', '20+', <TrainingMethodsTable sportName={sportDisplayName} />, '#e8f0fe', '#c8dafc')}
            {renderRubricAccordion('Enjoyment and Entertainment', '10+', <EnjoymentEntertainmentTable />, '#fff3e0', '#ffe0b2')}
            {isBadmintonSport ? (
              renderRubricAccordion('Strength and Conditioning', '12+', <BadmintonStrengthConditioningTable />, '#f1f8e9', '#dcedc8')
            ) : (
              renderTrainingPreviewSection('Strength and Conditioning', 'No strength and conditioning items published yet.', strengthConditioningPreview, '#f1f8e9', '#dcedc8')
            )}
            {isBadmintonSport ? (
              renderRubricAccordion('Mental Health', '8+', <BadmintonMentalHealthSection />, '#ede7f6', '#d1c4e9')
            ) : (
              renderTrainingPreviewSection('Mental Health', 'No mental health items published yet.', mentalHealthPreview, '#ede7f6', '#d1c4e9')
            )}
            {renderTrainingPreviewSection('Self Responsibility', 'No self responsibility items published yet.', selfResponsibilityPreview, '#fff8e1', '#ffe082')}
            {isBadmintonSport ? (
              renderRubricAccordion('Player Misconduct', '8+', <BadmintonPlayerMisconductTable />, '#e0f2f1', '#b2dfdb')
            ) : (
              renderTrainingPreviewSection('Rules and Fitness', 'No rules and fitness items published yet.', rulesFitnessPreview, '#e0f2f1', '#b2dfdb')
            )}
            {renderTrainingPreviewSection('Practice at Backyard/Home', 'No self-practice items published yet.', selfPracticePreview, '#e8f5e9', '#c8e6c9')}
            {renderTrainingPreviewSection('STEM Learning', 'No STEM learning items published yet.', stemLearningPreview, '#e8eaf6', '#c5cae9')}
            {renderTrainingPreviewSection('Data Analytics and AI', 'No data analytics and AI items published yet.', dataAnalyticsAiPreview, '#e0f7fa', '#b2ebf2')}
            {renderTrainingPreviewSection('Milestone Leaderboard', 'No milestone leaderboard items published yet.', milestoneLeaderboardPreview, '#f3e5f5', '#e1bee7')}
            {renderTrainingPreviewSection('Team and League', 'No team and league items published yet.', teamLeaguePreview, '#fffde7', '#fff9c4')}
            {renderTrainingPreviewSection('Tournament', 'No tournament items published yet.', tournamentPreview, '#fce4ec', '#f8bbd0')}
          </Box>
            </>
          )}

          {rubricTabValue === 1 && (
            <Box sx={{ maxWidth: 560 }}>
              <Box sx={{ border: '1px solid #dbe7f3', borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.75)', p: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f4c81', mb: 1 }}>
                  Assessment
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f4c81', mb: 0.75 }}>
                  Assess online or Manual Forms
                </Typography>
                {sportDisplayName.toLowerCase() === 'badminton' && (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={badmintonFramework}
                      onChange={(_, value) => {
                        if (value) setBadmintonFramework(value);
                      }}
                      sx={{
                        width: '100%',
                        bgcolor: '#e8f0fe',
                        borderRadius: 1.5,
                        p: 0.25,
                        mb: badmintonFramework === 'para' ? 1 : 1,
                        '& .MuiToggleButton-root': {
                          flex: 1,
                          fontWeight: 700,
                          border: 0,
                          color: '#1f2937',
                          textTransform: 'none',
                          bgcolor: 'transparent',
                        },
                        '& .MuiToggleButton-root.Mui-selected': {
                          bgcolor: '#cfe0fb',
                          color: '#0f4c81',
                        },
                      }}
                    >
                      <ToggleButton value="regular">Regular</ToggleButton>
                      <ToggleButton value="para">Para</ToggleButton>
                    </ToggleButtonGroup>
                    {badmintonFramework === 'para' && (
                      <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                        <InputLabel>Para Class</InputLabel>
                        <Select
                          value={badmintonParaClass}
                          label="Para Class"
                          onChange={(e) => setBadmintonParaClass(e.target.value)}
                        >
                          <MenuItem value="WH1">WH1</MenuItem>
                          <MenuItem value="WH2">WH2</MenuItem>
                          <MenuItem value="SL3">SL3</MenuItem>
                          <MenuItem value="SL4">SL4</MenuItem>
                          <MenuItem value="SU5">SU5</MenuItem>
                          <MenuItem value="SH6">SH6</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                  <Button size="small" variant="outlined" onClick={() => navigate('/login')} sx={{ textTransform: 'none' }}>
                    Login
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleDownloadSportAssessmentForm(
                        'beginner',
                        sportDisplayName.toLowerCase() === 'badminton' ? badmintonFramework : 'regular',
                        sportDisplayName.toLowerCase() === 'badminton' ? badmintonParaClass : 'WH1'
                      )
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Beginner
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleDownloadSportAssessmentForm(
                        'advanced',
                        sportDisplayName.toLowerCase() === 'badminton' ? badmintonFramework : 'regular',
                        sportDisplayName.toLowerCase() === 'badminton' ? badmintonParaClass : 'WH1'
                      )
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Advanced
                  </Button>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f4c81', mb: 0.75 }}>
                  Online Tests
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate('/login')}
                    sx={{ bgcolor: '#245c9f', '&:hover': { bgcolor: '#1b4678' } }}
                  >
                    Basic Skill Test
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate('/login')}
                    sx={{ bgcolor: '#0f4c81', '&:hover': { bgcolor: '#0b3a63' } }}
                  >
                    Advanced Skill Set
                  </Button>
                  {sportDisplayName.toLowerCase() === 'badminton' && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/login')}
                        sx={{ borderColor: '#0f4c81', color: '#0f4c81' }}
                      >
                        Fitness Test (Age 6-9)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/login')}
                        sx={{ borderColor: '#1b6b5b', color: '#1b6b5b' }}
                      >
                        Nutrition Test (Age 6-9)
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          </>
          )}
        </TabPanel>
        )}

        {/* Learning Tab */}
        {activeModuleId === 'learning' && (
        <TabPanel value={0} index={0}>
          {!sportModuleEnabled('learning') ? (
            <SportModuleComingSoon
              sport={sportDisplayName}
              moduleLabel="Learning"
              state={sportModuleState('learning')}
              notes={sportModuleMatrix?.matrix?.[sportDisplayName]?.learning?.visibilityNotes}
            />
          ) : (
          <>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1976d2' }}>
            Learning Resources
          </Typography>

          {/* Learning Sub-Tabs - Only for Badminton */}
          {sportDisplayName.toLowerCase() === 'badminton' ? (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={learningTabValue}
                  onChange={handleLearningTabChange}
                  aria-label="learning sub-tabs"
                  variant={isMobile ? 'scrollable' : 'standard'}
                  scrollButtons="auto"
                >
                  <Tab label="Strokes" id="learning-tab-0" aria-controls="learning-tabpanel-0" />
                  <Tab label="Drills" id="learning-tab-1" aria-controls="learning-tabpanel-1" />
                  <Tab label="Fitness" id="learning-tab-2" aria-controls="learning-tabpanel-2" />
                </Tabs>
              </Box>

              {/* Strokes Tab */}
              <TabPanel value={learningTabValue} index={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                    {strokeTableLabels.sectionTitle} {strokeTypes.length > 0 && `(${strokeTypes.length})`}
                  </Typography>
                  {!isAuthenticated && (
                    <Alert severity="info" sx={{ maxWidth: 400 }}>
                      Login to view all strokes
                    </Alert>
                  )}
                </Box>

                {/* Active Filters Display */}
                {(filterCourtPosition || filterHalfSide || filterStrokeType || filterPlayType || filterShotType || filterDifficulty) && (
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    {filterCourtPosition && (
                      <Chip
                        label={`${strokeTableLabels.courtPosition}: ${filterCourtPosition}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => setFilterCourtPosition('')}
                      />
                    )}
                    {filterHalfSide && (
                      <Chip
                        label={`${strokeTableLabels.halfSide}: ${filterHalfSide}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => setFilterHalfSide('')}
                      />
                    )}
                    {filterStrokeType && (
                      <Chip
                        label={`${strokeTableLabels.strokeType}: ${filterStrokeType}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => setFilterStrokeType('')}
                      />
                    )}
                    {filterPlayType && (
                      <Chip
                        label={`${strokeTableLabels.playType}: ${filterPlayType}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => setFilterPlayType('')}
                      />
                    )}
                    {filterShotType && (
                      <Chip
                        label={`${strokeTableLabels.shotType}: ${filterShotType}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => setFilterShotType('')}
                      />
                    )}
                    {filterDifficulty && (
                      <Chip
                        label={`${strokeTableLabels.difficulty}: ${filterDifficulty}`}
                        color="primary"
                        variant="outlined"
                        onDelete={() => setFilterDifficulty('')}
                      />
                    )}
                  </Box>
                )}

                {/* Filters */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Filters
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.courtPosition}</InputLabel>
                        <Select
                          value={filterCourtPosition}
                          label={strokeTableLabels.courtPosition}
                          onChange={(e) => setFilterCourtPosition(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueValues('courtPosition').map((val: string) => (
                            <MenuItem key={val} value={val}>{val}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.halfSide}</InputLabel>
                        <Select
                          value={filterHalfSide}
                          label={strokeTableLabels.halfSide}
                          onChange={(e) => setFilterHalfSide(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueValues('halfSide').map((val: string) => (
                            <MenuItem key={val} value={val}>{val}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.strokeType}</InputLabel>
                        <Select
                          value={filterStrokeType}
                          label={strokeTableLabels.strokeType}
                          onChange={(e) => setFilterStrokeType(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueValues('strokeType').map((val: string) => (
                            <MenuItem key={val} value={val}>{val}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.playType}</InputLabel>
                        <Select
                          value={filterPlayType}
                          label={strokeTableLabels.playType}
                          onChange={(e) => setFilterPlayType(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueValues('playType').map((val: string) => (
                            <MenuItem key={val} value={val}>{val}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.difficulty}</InputLabel>
                        <Select
                          value={filterDifficulty}
                          label={strokeTableLabels.difficulty}
                          onChange={(e) => setFilterDifficulty(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="1">1 Star</MenuItem>
                          <MenuItem value="2">2 Stars</MenuItem>
                          <MenuItem value="3">3 Stars</MenuItem>
                          <MenuItem value="4">4 Stars</MenuItem>
                          <MenuItem value="5">5 Stars</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.shotType}</InputLabel>
                        <Select
                          value={filterShotType}
                          label={strokeTableLabels.shotType}
                          onChange={(e) => setFilterShotType(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {getUniqueValues('shotType').map((val: string) => (
                            <MenuItem key={val} value={val}>{val}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{strokeTableLabels.difficulty}</InputLabel>
                        <Select
                          value={filterDifficulty}
                          label={strokeTableLabels.difficulty}
                          onChange={(e) => setFilterDifficulty(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="1">1 Star</MenuItem>
                          <MenuItem value="2">2 Stars</MenuItem>
                          <MenuItem value="3">3 Stars</MenuItem>
                          <MenuItem value="4">4 Stars</MenuItem>
                          <MenuItem value="5">5 Stars</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setFilterCourtPosition('');
                          setFilterHalfSide('');
                          setFilterStrokeType('');
                          setFilterPlayType('');
                          setFilterShotType('');
                          setFilterDifficulty('');
                        }}
                        sx={{ height: '40px' }}
                      >
                        Clear All Filters
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {loadingStrokes ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : strokeTypes.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {strokeTypes.map((strokeType) => renderLearningStrokeRow(strokeType))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No stroke types available at the moment.
                  </Alert>
                )}
              </TabPanel>

              {/* Drills Tab */}
              <TabPanel value={learningTabValue} index={1}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1976d2' }}>
                  Training Drills
                </Typography>
                <Alert severity="info">
                  Training drills content will be available soon. Practice with structured drills designed to improve your skills.
                </Alert>
              </TabPanel>

              {/* Fitness Tab */}
              <TabPanel value={learningTabValue} index={2}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1976d2' }}>
                  Fitness Training
                </Typography>
                <Alert severity="info">
                  Fitness training content will be available soon. Learn about fitness exercises and training routines for badminton players.
                </Alert>
              </TabPanel>
            </>
          ) : (
            <Alert severity="info">
              Learning resources are currently available for Badminton only.
            </Alert>
          )}
          </>
          )}
        </TabPanel>
        )}

        {/* Player Rankings Tab */}
        {activeModuleId === 'ranking' && (
        <TabPanel value={0} index={0}>
          {!sportModuleEnabled('ranking') ? (
            <SportModuleComingSoon
              sport={sportDisplayName}
              moduleLabel="Player Rankings"
              state={sportModuleState('ranking')}
              notes={sportModuleMatrix?.matrix?.[sportDisplayName]?.ranking?.visibilityNotes}
            />
          ) : (
          <>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1976d2' }}>
            Player Rankings
          </Typography>

          {/* Ranking Type Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={rankingTypeTab}
              onChange={handleRankingTypeTabChange}
              aria-label="ranking type tabs"
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
            >
              <Tab label="Coach/Academy Evaluation" id="ranking-tab-0" aria-controls="ranking-tabpanel-0" />
              <Tab label="Board Exam" id="ranking-tab-1" aria-controls="ranking-tabpanel-1" />
            </Tabs>
          </Box>

          {/* Active Filters Display */}
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Chip
              label={rankingLevel === 'national' ? 'National Level' : 'State Level'}
              color="primary"
              onDelete={() => removeFilter('rankingLevel')}
              sx={{ fontWeight: 600 }}
            />
            {selectedCountry && (
              <Chip
                label={`Country: ${selectedCountry}`}
                color="primary"
                variant="outlined"
                onDelete={() => removeFilter('country')}
              />
            )}
            {selectedState && (
              <Chip
                label={`State: ${selectedState}`}
                color="primary"
                variant="outlined"
                onDelete={() => removeFilter('state')}
              />
            )}
            {selectedAgeCategory && (
              <Chip
                label={`Age: ${selectedAgeCategory}`}
                color="primary"
                variant="outlined"
                onDelete={() => removeFilter('ageCategory')}
              />
            )}
            {selectedYear && (
              <Chip
                label={`Year: ${selectedYear}`}
                color="primary"
                variant="outlined"
                onDelete={() => removeFilter('year')}
              />
            )}
            {selectedGender && selectedGender !== 'All' && (
              <Chip
                label={`Gender: ${selectedGender}`}
                color="primary"
                variant="outlined"
                onDelete={() => removeFilter('gender')}
              />
            )}
            {playerNameSearch && (
              <Chip
                label={`Search: ${playerNameSearch}`}
                color="primary"
                variant="outlined"
                onDelete={() => removeFilter('playerName')}
              />
            )}
          </Box>
          
          {/* Ranking Level and Filters */}
          <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Ranking Level</InputLabel>
              <Select
                value={rankingLevel}
                label="Ranking Level"
                onChange={handleRankingLevelChange}
              >
                <MenuItem value="national">National Level</MenuItem>
                <MenuItem value="state">State Level</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                label="Country"
                onChange={handleCountryChange}
              >
                <MenuItem value="">
                  <em>All Countries</em>
                </MenuItem>
                {COUNTRIES.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl 
              sx={{ minWidth: 200 }} 
              disabled={!selectedCountry || rankingLevel === 'national'}
            >
              <InputLabel>State/Province</InputLabel>
              <Select
                value={selectedState}
                label="State/Province"
                onChange={handleStateChange}
              >
                <MenuItem value="">
                  <em>All States</em>
                </MenuItem>
                {availableStates.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Age Category</InputLabel>
              <Select
                value={selectedAgeCategory}
                label="Age Category"
                onChange={handleAgeCategoryChange}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {ageCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={handleYearChange}
              >
                <MenuItem value="">
                  <em>All Years</em>
                </MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Yearly Period</InputLabel>
              <Select
                value={rankingPeriodKey}
                label="Yearly Period"
                onChange={(e) => setRankingPeriodKey(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>Latest submitted</em>
                </MenuItem>
                {selectedYear && (
                  <MenuItem value={`Y-${selectedYear}`}>{`Yearly ${selectedYear}`}</MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={selectedGender}
                label="Gender"
                onChange={handleGenderChange}
              >
                {genderOptions.map((gender) => (
                  <MenuItem key={gender} value={gender}>
                    {gender}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Search Player Name"
              value={playerNameSearch}
              onChange={handlePlayerNameSearchChange}
              placeholder="Enter player name..."
              sx={{ minWidth: 200 }}
              size="small"
            />
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Player Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Strokes Score</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Drill Score</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Fitness Score</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Age Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Academy</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRankings ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : mockRankings.length > 0 ? (
                  mockRankings.map((row) => (
                    <TableRow key={`${row.rank}-${row.playerName}`} hover>
                      <TableCell>
                        <Chip
                          label={row.rank}
                          color={row.rank <= 3 ? 'primary' : 'default'}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.playerName}</TableCell>
                      <TableCell>{row.score}%</TableCell>
                      <TableCell>{(row as any).drillScore != null && (row as any).drillScore !== '' ? `${(row as any).drillScore}%` : '-'}</TableCell>
                      <TableCell>{(row as any).fitnessScore != null && (row as any).fitnessScore !== '' ? `${(row as any).fitnessScore}%` : '-'}</TableCell>
                      <TableCell>{row.ageCategory}</TableCell>
                      <TableCell>{row.academy}</TableCell>
                      <TableCell>{(row as any).school || ''}</TableCell>
                      <TableCell>{row.country}</TableCell>
                      <TableCell>{row.state}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No players found for the selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </>
          )}
        </TabPanel>
        )}

        {/* Contest Tab */}
        {activeModuleId === 'contests' && (
        <TabPanel value={0} index={0}>
          {!sportModuleEnabled('contests') ? (
            <SportModuleComingSoon
              sport={sportDisplayName}
              moduleLabel="Contests"
              state={sportModuleState('contests')}
              notes={sportModuleMatrix?.matrix?.[sportDisplayName]?.contests?.visibilityNotes}
            />
          ) : (
          <>
          <ContestHubHeader sportName={sportDisplayName} />
          <ContestSponsorBanner sportName={sportDisplayName} />

          {renderFeaturedSmashContest()}

          {loadingContestTasks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : contestTasks.length === 0 ? (
            <Alert severity="info">
              More public contest tasks will be published here for {sportDisplayName}.
            </Alert>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f4c81', mb: 2 }}>
                Published Contest List
              </Typography>
              <Grid container spacing={2}>
                {contestTasks.map((task) => (
                  <Grid item xs={12} key={task.id}>
                    {renderContestTaskCard(task)}
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          </>
          )}
        </TabPanel>
        )}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#263238', color: 'white', py: 4, mt: 8, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © 2024 EncycGames. Empowering coaches and players through comprehensive performance tracking.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default SportPage;

